import React, { createContext, useContext, useEffect, useMemo } from "react";
import { ConfigProvider } from "antd";
import { getTenantPalette, ITenantThemeConfig, IColorPalette } from "./tenantTheme.config";

interface IThemeContext {
  palette: ITenantThemeConfig;
  primaryColor: string;
  secondaryColor: string;
}

const ThemeContext = createContext<IThemeContext | null>(null);

/**
 * Apply color palette as CSS variables to document root
 */
const applyPaletteCSSVariables = (palette: ITenantThemeConfig): void => {
  const root = document.documentElement;

  // Apply primary palette
  Object.entries(palette.primary).forEach(([shade, color]) => {
    root.style.setProperty(`--primary-${shade}`, color);
  });

  // Apply secondary palette
  Object.entries(palette.secondary).forEach(([shade, color]) => {
    root.style.setProperty(`--secondary-${shade}`, color);
  });

  // Set main brand colors (shade 6) for backward compatibility
  root.style.setProperty("--primary-color", palette.primary[6]);
  root.style.setProperty("--secondary-color", palette.secondary[6]);
};

interface ThemeProviderProps {
  children: React.ReactNode;
  tenantId?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, tenantId }) => {
  const palette = useMemo(() => getTenantPalette(tenantId), [tenantId]);

  // Apply CSS variables when palette changes
  useEffect(() => {
    applyPaletteCSSVariables(palette);
  }, [palette]);

  const contextValue: IThemeContext = useMemo(
    () => ({
      palette,
      primaryColor: palette.primary[6],
      secondaryColor: palette.secondary[6]
    }),
    [palette]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useTheme = (): IThemeContext => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/**
 * Hook to get specific palette colors
 */
export const usePrimaryPalette = (): IColorPalette => {
  const { palette } = useTheme();
  return palette.primary;
};

export const useSecondaryPalette = (): IColorPalette => {
  const { palette } = useTheme();
  return palette.secondary;
};

export default ThemeProvider;
